import React, { useState, useMemo } from "react";
import {
  Slate,
  Editable,
  withReact,
  RenderLeafProps,
  RenderElementProps,
} from "slate-react";
import { withHistory } from "slate-history";
import { Box, Typography } from "@mui/material";
import { Descendant, createEditor } from "slate";
import { makeStyles, createStyles } from "@mui/styles";

import { Toolbar } from "./Toolbar";
import { jsonSlateToHtml } from "./serialize";
import { htmlToJsonSlate } from "./deserialize";
import { withLinks } from "./components/LinkButton/linkButton";

const useStyles = makeStyles(() =>
  createStyles({
    editable: {
      minHeight: "100px !important",
      padding: "1px 10px",
      border: "1px solid #b5b5b5",
      borderTop: 0,
      borderBottomLeftRadius: "4px",
      borderBottomRightRadius: "4px",
    },

    editableError: {
      minHeight: "100px !important",
      padding: "1px 10px",
      border: `1px solid #F50000`,
      borderTop: 0,
      borderBottomLeftRadius: "4px",
      borderBottomRightRadius: "4px",
    },
  })
);

interface IRichTextProps {
  value: string;
  error?: boolean;
  disabled?: boolean;
  helperText?: string;
  disableMacros?: boolean;
  setValue(value: string): void;
  macroType: "Terms" | "Orientation" | "Email";
}

export const RichText: React.FC<IRichTextProps> = ({
  value,
  setValue,
  macroType,
  helperText,
  disableMacros,
  error = false,
  disabled = false,
}) => {
  const editor = useMemo(
    () => withLinks(withHistory(withReact(createEditor()))),
    []
  );
  const [initialValue, setInitialValue] = useState<Descendant[]>(
    htmlToJsonSlate(value)
  );

  const { editable, editableError } = useStyles();

  return (
    <>
      <Slate
        editor={editor}
        value={initialValue}
        onChange={(value) => setInitialValue(value)}
      >
        <Toolbar
          error={error}
          disabled={disabled}
          macroType={macroType}
          disableMacros={disableMacros ? true : false}
        />
        <Editable
          disabled={disabled}
          readOnly={disabled}
          placeholder="Adicione algum texto..."
          renderLeaf={(props) => <Leaf {...props} />}
          className={error ? editableError : editable}
          renderElement={(props) => <Element {...props} />}
          onBlur={() => setValue(jsonSlateToHtml(initialValue))}
        />
      </Slate>

      {helperText && (
        <Box paddingLeft={1.6} marginTop={0.2}>
          <Typography variant="caption" color={error ? "error" : "textPrimary"}>
            {helperText}
          </Typography>
        </Box>
      )}
    </>
  );
};

const Element = (props: RenderElementProps) => {
  switch (props.element.type) {
    case "text-align":
      return (
        <p
          {...props.attributes}
          style={{
            textAlign: props.element.align as CanvasTextAlign,
          }}
        >
          {props.children}
        </p>
      );
    case "link":
      return (
        <a {...props.attributes} href={props.element.url}>
          {props.children}
        </a>
      );
    default:
      return <p {...props.attributes}>{props.children}</p>;
  }
};

const Leaf = (props: RenderLeafProps) => {
  let { children } = props;

  if (props.leaf.bold) {
    children = <strong>{children}</strong>;
  }

  if (props.leaf.italic) {
    children = <em>{children}</em>;
  }

  if (props.leaf.underline) {
    children = <u>{children}</u>;
  }

  if (props.leaf.fontSize) {
    children = (
      <span style={{ fontSize: props.leaf.fontSize }}>{children}</span>
    );
  }

  if (props.leaf.color) {
    children = <span style={{ color: props.leaf.color }}>{children}</span>;
  }

  if (props.leaf.background) {
    children = (
      <span style={{ backgroundColor: props.leaf.background }}>{children}</span>
    );
  }

  return <span {...props.attributes}>{children}</span>;
};
